import {
  Button,
  CellFormatOptions,
  ColumnTable,
  Table,
} from '@octano/global-ui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { format as formatRut } from 'rut.js';

import {
  OnlineAssistanceRequirement,
  Section,
  Student,
} from '../../../api/requests/teacherPortal';
import TableEmptyContent from '../../../components/Text/TableEmptyContent';

export interface TablePeriodEndReviewProps {
  data: Student[];
  section: Section;
  passingGrade: number;
  onlineAssistanceRequirement?: OnlineAssistanceRequirement;
  onPressImportAssistance?: () => void;
}

export default function TablePeriodEndReview(props: TablePeriodEndReviewProps) {
  const { t } = useTranslation();
  const {
    data,
    passingGrade,
    section,
    onlineAssistanceRequirement,
    onPressImportAssistance,
  } = props;

  const studentColumn = useMemo(() => {
    return {
      columnName: 'student',
      headerText: t('periodEndReview.table.student'),
      tdClassName: 'text-center  column-sticky bg-white',
      thClassName: 'text-center  column-sticky bg-secondary',
      width: '280px',
      cellFormat: ({ row }: CellFormatOptions<Student>) => {
        return (
          <span>
            {row.fullName}
            <br />
            {row.run ? formatRut(row.run) : row.passportNumber}
          </span>
        );
      },
    };
  }, [t]);

  const finalGradeColumn = useMemo(() => {
    return {
      columnName: 'finalGrade',
      headerText: t('periodEndReview.table.gradeFinal'),
      tdClassName: 'text-center',
      thClassName: 'text-center',
      cellFormat: ({ row }: CellFormatOptions<Student>) => {
        let textColor;
        if (row.grades.finalGrade === 'NR' || row.grades.finalGrade === 'P') {
          textColor = row.grades.finalGrade !== 'P' && 'text-danger';
        } else {
          textColor =
            Number(row.grades.finalGrade) >= passingGrade
              ? 'text-primary'
              : 'text-danger';
        }

        return <span className={textColor || ''}>{row.grades.finalGrade}</span>;
      },
    };
  }, [t, passingGrade]);

  const assistanceRequirementsColumn = useMemo(() => {
    return section.assistanceRequirements
      .filter((r) => r.minPercentage > 0)
      .map(({ activityType, minPercentage, activityTypeId }) => {
        return {
          columnName: activityType.name,
          headerText: `${t('periodEndReview.table.assistances')} ${
            activityType.name
          } ${minPercentage}%`,
          tdClassName: 'text-center',
          thClassName: 'text-center',
          cellFormat: ({ row }: CellFormatOptions<Student>) => {
            return (
              <span
                className={
                  Number(row.assistances[activityTypeId].percentage) >=
                  minPercentage
                    ? 'text-primary'
                    : 'text-danger'
                }
              >
                {Number(row.assistances[activityTypeId].percentage).toFixed(1)}%
              </span>
            );
          },
        };
      });
  }, [section, t]);

  const onlineAssistanceColumn = useMemo(() => {
    return {
      columnName: 'onlineAssistance',
      width: 120,
      headerText: (
        <div className="d-flex flex-column">
          <span>{t('periodEndReview.table.assistances')}</span>
          <span>
            {t('periodEndReview.table.onlineAssistance', {
              value: Number(
                (onlineAssistanceRequirement?.value ?? 0) / 100,
              ).toLocaleString('es-CL', {
                style: 'percent',
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
              }),
            })}
          </span>
          {!!onPressImportAssistance && (
            <Button
              size="sm"
              text={t('periodEndReview.importAssistance')}
              onClick={onPressImportAssistance}
              disabled={!!onlineAssistanceRequirement?.lastImportedAt}
            />
          )}
        </div>
      ),
      tdClassName: 'text-center',
      thClassName: 'text-center',
      cellFormat: ({ row }: CellFormatOptions<Student>) => {
        const onlineData = row.onlineAssistance;

        // Caso 1: No configurado o sin datos
        if (!onlineAssistanceRequirement?.required) {
          return <span>-</span>;
        }

        // Caso 2: Pendiente de importación
        if (!onlineData || onlineData.value === null) {
          return <span className="text-warning">Pendiente de importación</span>;
        }

        // Caso 3: Importado - mostrar el valor
        const textColor =
          onlineData.value >= (onlineAssistanceRequirement?.value ?? 0)
            ? 'text-primary'
            : 'text-danger';

        return (
          <span className={textColor}>
            {Number((onlineData.value ?? 0) / 100).toLocaleString('es-CL', {
              style: 'percent',
              minimumFractionDigits: 0,
              maximumFractionDigits: 2,
            })}
          </span>
        );
      },
    };
  }, [onlineAssistanceRequirement, onPressImportAssistance, t]);

  const stateColumn = useMemo(() => {
    return {
      columnName: 'state',
      headerText: t('periodEndReview.table.state'),
      tdClassName: 'text-center',
      thClassName: 'text-center',
      cellFormat: ({ row }: CellFormatOptions<Student>) => {
        const textColor =
          row.generalPassed === 'PASSED'
            ? 'text-success'
            : row.generalPassed && 'text-danger';
        return (
          <span className={textColor || ''}>
            {t(
              `periodEndReview.table.sectionStudentSummaryStateEnum.${
                row.generalPassed || 'PENDING'
              }`,
            )}
          </span>
        );
      },
    };
  }, [t]);

  const columns = useMemo<ColumnTable[]>(() => {
    // Solo mostrar la columna si el servicio indica explícitamente required: true.
    // Esto filtra secciones presenciales donde el campo vale 0 (sin requisito).
    const showOnlineAssistance = onlineAssistanceRequirement?.required === true;

    return [
      studentColumn,
      finalGradeColumn,
      ...assistanceRequirementsColumn,
      ...(showOnlineAssistance ? [onlineAssistanceColumn] : []),
      stateColumn,
    ];
  }, [
    finalGradeColumn,
    assistanceRequirementsColumn,
    stateColumn,
    studentColumn,
    onlineAssistanceColumn,
    onlineAssistanceRequirement,
  ]);

  const noResultsText = useMemo(() => {
    return (
      <TableEmptyContent
        icon="information"
        title={t('periodEndReview.table.notResult.empty')}
        subtitle=""
      />
    );
  }, [t]);

  return (
    <Table
      height={500}
      columns={columns}
      data={data}
      noResultsText={noResultsText}
      borderless={false}
      striped={false}
    />
  );
}
